import React from 'react';
import { useTranslation } from 'react-i18next';

import logo from '../../../../res/images/logo.svg';

/**
 * One row of the bell's dropdown — Figma 16370:192973: the picture at the
 * inline start, the title, the time it arrived and the body beside it, and the
 * unread dot at the far edge, all centred on the row.
 *
 * The dashboard's panel draws notifications as a card instead — see
 * `sections/NotificationTile`. The two share the hook, not the markup.
 *
 * An unread one is marked three ways, as the design draws it: a pale blue
 * ground (blue/250), a blue dot, and its time lettered in the brand blue
 * rather than gray/600. The ground alone is a difference in colour; a reader
 * who cannot see that still gets the dot.
 *
 * The dot keeps its 8px on a read row too, empty, so the text column starts at
 * the same place on every row — the design leaves the frame there for the
 * same reason.
 *
 * The stamp is written with `toLocaleString` under the reading language, which
 * is what puts an Arabic reader's date in Arabic. The design's own sample
 * ("9/2/2026, 7:28:04 PM") is the browser's US default, not a format to copy.
 * It sits in a `<bdi>` so its pieces keep their order inside an Arabic line
 * without the paragraph around it turning left-aligned.
 *
 * The picture falls back to the site mark. `imgUrl` is null on every
 * notification the admin panel sends without one, and an <img> with an empty
 * src draws the browser's broken-image glyph.
 */
const NotificationItem = ({ notification }) => {
    const { i18n } = useTranslation();

    const { title, content, imgUrl, creatAt, isUnread, href } = notification;
    const stamp = creatAt ? new Date(creatAt).toLocaleString(i18n.language) : '';

    return (
        <a
            href={href}
            className={
                'tw-flex tw-items-center tw-gap-[12px] tw-p-[16px] tw-transition-colors ' +
                'tw-no-underline hover:tw-no-underline focus:tw-no-underline ' +
                'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset ' +
                'focus-visible:tw-ring-v2-brand ' +
                (isUnread ? 'tw-bg-v2-brand-panel' : 'tw-bg-white hover:tw-bg-v2-neutral-50')
            }
        >
            <img
                src={imgUrl || logo}
                alt=""
                aria-hidden
                loading="lazy"
                className="tw-h-[73px] tw-w-[81px] tw-shrink-0 tw-rounded-[4px] tw-object-cover"
            />

            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[4px] tw-text-start">
                <div>
                    <p className="tw-m-0 tw-mb-[-2px] tw-truncate tw-text-[15px] tw-font-bold tw-leading-[1.5] tw-text-v2-neutral-900">
                        {title}
                    </p>

                    <p
                        className={
                            'tw-m-0 tw-text-[11px] tw-leading-[1.5] ' +
                            (isUnread ? 'tw-text-v2-brand' : 'tw-text-v2-text-faint')
                        }
                    >
                        <bdi dir="auto">{stamp}</bdi>
                    </p>
                </div>

                <p className="tw-m-0 tw-line-clamp-2 tw-text-[13px] tw-leading-[1.6] tw-text-v2-student-meta">
                    {content}
                </p>
            </div>

            <span
                aria-hidden
                className={'tw-size-[8px] tw-shrink-0 tw-rounded-full ' + (isUnread ? 'tw-bg-v2-brand' : '')}
            />
        </a>
    );
};

export default NotificationItem;
